import React, { useState, useEffect, useCallback } from "react";
import {
  useAppUserManager,
  useOrgManager,
  usePayoutManager,
  useSchoolManager,
  useDistrictManager,
} from "../../../../hooks/useManagers";
import MainLayout from "../../../../components/layout/MainLayout";
import PayoutAdminSettings from "../../components/payout/PayoutAdminSettings";
import { useParams, useLocation } from "react-router-dom";
import { useNavigate } from "../../../../components/Navigate";
import Loading from "../../../../components/Loading";
import { ReportingService } from "../../../../services/ReportingService";
import { useHookstate } from "@hookstate/core";
import globalAuthStateStore from "../../../../stores/globalAuthStateStore";
import { DialogControl } from "../../../../components/controls/DialogControl";
import { SelectListControl } from "../../../../components/controls";

export default function PayoutAdminConfigMgmt() {
  const location = useLocation();
  const params = useParams();
  const schoolMgr = useSchoolManager();
  const districtMgr = useDistrictManager();
  const navigate = useNavigate();
  const payoutMgr = usePayoutManager();
  const orgMgr = useOrgManager();
  const authStore = useHookstate(globalAuthStateStore);
  var reportingSvc = new ReportingService(authStore);
  const appUserMgr = useAppUserManager();
  const [orgs, setOrgs] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [errors, setErrors] = useState(null);
  const [recipientResults, setRecipientResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [payoutAdminConfigMetricsData, setPayoutAdminConfigMetricsData] =
    useState();
  const [payoutAdminConfig, setPayoutAdminConfig] = useState();
  const [payoutTeacherConfig, setPayoutTeacherConfig] = useState();
  const [exportTypeDialog, setExportTypeDialog] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState("PDF");
  const [payoutInfo, setPayoutInfo] = useState(null);
  const [payoutSchoolItems, setPayoutSchoolItems] = useState([]);

  const loadRecipientData = useCallback(async () => {
    const clientId = payoutInfo?.ClientId;
    const schoolId = payoutInfo?.SchoolId;
    const districtId = payoutInfo?.DistrictId;
    let payoutRecipients;
    let errors = false;
    let result;
    const payoutSchoolId = params?.payoutId;

    try {
      if (!payoutInfo || payoutInfo.PayoutStatus == "Not Started") {
        result = await payoutMgr.LoadPayoutSchools(
          payoutSchoolId,
          schoolId,
          districtId,
          1
        );
      }

      const isCompleted = result?.first()?.PayoutStatus
        ? result?.first()?.PayoutStatus == 3
        : payoutInfo?.PayoutStatus == 3 ||
          payoutInfo?.PayoutStatus === "Completed";

      const payoutAdminsResponse = await payoutMgr.GetPayoutAdmins(
        clientId,
        districtId,
        false,
        isCompleted
      );

      if (payoutAdminsResponse.Success) {
        payoutRecipients = payoutAdminsResponse.Items;
      }

      if (!payoutAdminConfig && !payoutAdminConfigMetricsData) {
        const response = await payoutMgr.GetAdminPayoutConfiguration(
          payoutAdminsResponse?.Items?.first().ClientId,
          payoutAdminsResponse?.Items?.first().DistrictId,
          payoutAdminsResponse?.Items?.first().SchoolId
        );

        if (response.Items?.length > 0) {
          const metricSet = response.Items[0].PayoutAdminConfigurationMetric;
          setPayoutAdminConfigMetricsData(metricSet);
          setPayoutAdminConfig(response.Items[0]);
        }
      } else if (!payoutAdminsResponse.Success) {
        errors = true;
      }

      if (!payoutInfo) {
        setPayoutInfo(result.first());
      }

      if (!errors) {
        setRecipientResults(payoutRecipients);
      }
    } catch (error) {
      console.error("Error loading recipient data:", error);
      setErrors(["Failed to load recipient data"]);
      errors = true;
    } finally {
      setIsLoading(false);
    }
  }, [
    payoutInfo,
    params?.payoutId,
    payoutMgr,
    payoutAdminConfig,
    payoutAdminConfigMetricsData,
    payoutTeacherConfig,
    navigate,
    setErrors,
    setIsLoading,
    setPayoutAdminConfigMetricsData,
    setPayoutAdminConfig,
    setPayoutTeacherConfig,
    setPayoutInfo,
    setRecipientResults,
  ]);

  const loadPayoutConfig = useCallback(
    async (payoutConfigId) => {
      try {
        if (!payoutAdminConfig) {
          const config = await payoutMgr._payoutAdminConfigurationItemMgr.query(
            "x => x.Id == payoutConfigId",
            { payoutConfigId }
          );

          if (config.Items.length > 0) {
            const configItem = config.Items.first();
            const response = await payoutMgr.GetAdminPayoutConfiguration(
              configItem.ClientId,
              configItem.DistrictId,
              configItem.SchoolId
            );

            if (response.Items?.length > 0) {
              setPayoutAdminConfig(response.Items[0]);
            }

            const schoolYear = configItem.SchoolYearIdentifier;

            const schoolsResult = await payoutMgr.LoadPayoutSchools(
              undefined,
              configItem.SchoolId,
              configItem.DistrictId,
              1
            );

            const districtItems = Array.isArray(schoolsResult)
              ? schoolsResult.filter((r) => r.DistrictId === configItem.DistrictId)
              : [];

            if (districtItems.length > 0) {
              setPayoutSchoolItems(districtItems);
              setPayoutInfo(districtItems.first());
            } else {
              setErrors(["No payout records found for this district"]);
              setPayoutSchoolItems([]);
            }
          }
        }
      } catch (error) {
        console.error("Error loading payout config:", error);
        setErrors(["Failed to load payout configuration"]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      payoutTeacherConfig,
      payoutAdminConfig,
      payoutMgr,
      schoolMgr,
      setPayoutTeacherConfig,
      setPayoutAdminConfig,
      setPayoutInfo,
      setPayoutSchoolItems,
      setErrors,
      setIsLoading,
    ]
  );

  const handleDataProperties = useCallback(() => {
    setIsLoading(true);

    if (!payoutMgr || !appUserMgr || !orgMgr) return;

    if (params?.payoutConfigId) {
      loadPayoutConfig(params.payoutConfigId);
    } else if (params?.payoutId) {
      // Logic for the Recipients page
      loadRecipientData();
    } else {
      // Logic for other pages or default behavior
      setPayoutAdminConfigMetricsData(null);
      setPayoutAdminConfig(null);
      setPayoutTeacherConfig(null);

      if (!orgs) {
        orgMgr.loadOrgTreeWithPayouts("district").then((items) => {
          setOrgs(items);
        });
      }

      payoutMgr
        .LoadPayoutSchools(undefined, undefined, undefined, 1)
        .then((r) => {
          if (Array.isArray(r)) {
            setIsLoading(false);
          } else {
            // Set errors here
            setIsLoading(false);
          }
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [
    location.pathname,
    params,
    payoutMgr,
    appUserMgr,
    orgMgr,
    orgs,
    loadRecipientData,
    loadPayoutConfig,
    setIsLoading,
    setPayoutAdminConfigMetricsData,
    setPayoutAdminConfig,
    setPayoutTeacherConfig,
    setOrgs,
  ]);

  useEffect(() => {
    handleDataProperties();
  }, [params, location, payoutMgr, appUserMgr]);

  const handleCalculatePayout = (
    schoolId,
    schoolYearIdentifier,
    districtId,
    fileType
  ) => {
    setLoading(true);
    payoutMgr
      .CalculatePayout(schoolId, schoolYearIdentifier, districtId, true)
      .then((response) => {
        if (response.Success) {
          handleOnCalculate(districtId, schoolId, fileType);
        } else {
          setLoading(false);
          setErrors([
            "Failed to calculate payout. If this problem persist, please contact customer support.",
          ]);
        }
      });
  };

  const handleOnCalculate = async (districtId, schoolId, fileType) => {
    setLoading(true);
    const reportParams = {
      report: "SchoolAdministratorPayout.trdp",
      parameterValues: {
        CurrentUserId: null,
        CurrentUserTypeName: "AllData",
        DateRangeEnd: null,
        DateRangeStart: null,
        SchoolYear: parseInt(payoutMgr.AppUserState.selectedSchoolYear),
        DistrictID: districtId,
      },
    };

    const result = await reportingSvc.getExport(
      reportParams,
      fileType,
      exportTypeDialog.type
    );
    //result is undefined then its successful
    setLoading(false);
    if (result) {
      setErrors([
        "Calculations Succeeded, but unable to generate Payout Export.",
      ]);
    }
    setExportTypeDialog(false);
  };

  const savePayoutUsers = async (
    payoutRecipientsToSave,
    forceSave,
    isComplete
  ) => {
    if (payoutRecipientsToSave.length > 0) {
      const arrayOfPromises = [];
      for (const rowIndex of payoutRecipientsToSave) {
        populateOriginalWithCustom(rowIndex);
        if (rowIndex.isEdited || forceSave) {
          if (isComplete) rowIndex.CompletedDate = new Date();
          const recipientSaveResult = arrayOfPromises.push(
            payoutMgr._payoutAdminItemMgr.save(rowIndex)
          );
          if (recipientSaveResult) {
            console.log("save " + recipientSaveResult);
          }
        }
      }
      const results = await Promise.all(arrayOfPromises);
      if (results.every((x) => x.Success)) {
        return true;
      } else {
        setErrors(["Failed To save a recipient"]);
      }
    }
  };
  const populateOriginalWithCustom = (row) => {
    for (const key in row) {
      //If the currentValue is not null/undefined but the original value is, make it the original value.
      // Original. Skr score , Compass Equiv, surveyscore populated by backend disable until data..

      const adminOriginalCounterPart =
        key.startsWith("Custom") ||
        key === "Comment" ||
        key === "IsActive" ||
        key === "IsAssistantPrincipal" ||
        key === "PayoutPct" ||
        key === "PayoutPctReason" ||
        key === "SKRScore" ||
        key === "SurveyScore";
      // Override field is not null.
      // Original / Override field can't be negative.
      // Original Field does not equal Override field. (Handle both being nulls)
      // Original Field must be null/empty/""

      if (
        adminOriginalCounterPart &&
        row[key] !== null &&
        row[key] !== undefined &&
        row["Original" + key] !== row[key] &&
        (row["Original" + key] === null ||
          row["Original" + key] === undefined ||
          row["Original" + key] === "") &&
        row["Original" + key] >= 0 &&
        row[key] >= 0
      ) {
        row["Original" + key] = row[key];
        row.isEdited = true;
      }
    }
  };

  const handleOnComplete = async () => {
    if (payoutSchoolItems.length === 0) {
      setErrors(["No payout records found for this district"]);
      return;
    }

    savePayoutUsers(recipientResults, true, true);
    
    const savePromises = payoutSchoolItems.map((item) =>
      payoutMgr.savePayoutSchool({
        ...item,
        PayoutStatus: 3,
        PayoutDate: new Date(),
      })
    );

    await Promise.all(savePromises);
    navigate("/admin/Payout/Admin");
  };

  const handleOnPaymentSettingsPrevious = () => {
    setIsLoading(true);
    navigate(-1);
  };

  return (
    <>
      <DialogControl
        title={"Calculate as Admin Payout"}
        innerclassName={"dialog__inner__center"}
        openDialog={exportTypeDialog}
        okText={"Generate"}
        loadingOk={loading}
        enableNext={true}
        onCancel={() => {
          setExportTypeDialog(false);
        }}
        onOk={() => {
          //Payout represents calculate button.
          //User represents export button.
          //Params
          if (selectedFileType === "PDF") {
            if (params.payoutConfigId) {
              handleCalculatePayout(
                payoutInfo.School?.Id,
                payoutInfo.SchoolYearIdentifier,
                payoutInfo?.District?.Id,
                "PDF"
              );
            } else {
              handleCalculatePayout(
                exportTypeDialog.item.School?.Id,
                exportTypeDialog.item.SchoolYearIdentifier,
                exportTypeDialog.item.DistrictId,
                "PDF"
              );
            }
          } else if (selectedFileType === "EXCEL") {
            if (params.payoutConfigId) {
              handleCalculatePayout(
                payoutInfo.School?.Id,
                payoutInfo.SchoolYearIdentifier,
                payoutInfo?.District?.Id,
                "XLSX"
              );
            } else {
              handleCalculatePayout(
                exportTypeDialog.item.School?.Id,
                exportTypeDialog.item.SchoolYearIdentifier,
                exportTypeDialog.item.DistrictId,
                "XLSX"
              );
            }
          }
        }}
      >
        <SelectListControl
          field={{ fieldName: "filetype" }}
          value={selectedFileType}
          hidePleaseSelect={true}
          textValuePairs={[
            { text: "PDF", value: "PDF" },
            { text: "EXCEL", value: "EXCEL" },
          ]}
          onChange={(e) => {
            setSelectedFileType(e.target.value);
          }}
        />
      </DialogControl>
      <MainLayout errors={errors} validationErrors={validationErrors}>
        {isLoading ? (
          <div className="payout-page-loading">
            <Loading type="default" size="5rem" />
            <div className="loading-font">Loading</div>
          </div>
        ) : (
          <PayoutAdminSettings
            recipientResults={recipientResults}
            payoutMgr={payoutMgr}
            payoutInfo={payoutInfo}
            payoutConfig={payoutAdminConfig}
            setPayoutAdminConfigMetricsData={setPayoutAdminConfigMetricsData}
            setPayoutConfig={setPayoutAdminConfig}
            onPrevious={handleOnPaymentSettingsPrevious}
            onCaluclate={() =>
              setExportTypeDialog({
                item: payoutInfo,
                type: "Admin Payout Export",
              })
            }
            handleOnComplete={handleOnComplete}
            appUserMgr={appUserMgr}
            savePayoutUsers={savePayoutUsers}
            setValidationErrors={setValidationErrors}
            isCompleteDisabled={payoutSchoolItems.length === 0}
          />
        )}
      </MainLayout>
    </>
  );
}
